import { ManageRolesGuard } from '../core/guards/manage-roles.guard';
import { ManageUsersGuard } from '../core/guards/manage-users.guard';
import { UserComponent } from './user/user.component';
import { AdministratorListComponent } from './administrator-list/administrator-list.component';
import { CarrierDriverListComponent } from './carrier-driver-list/carrier-driver-list.component';
import { OverviewComponent } from './overview/overview.component';
import { DriverComponent } from './driver/driver.component';
import { CarrierListComponent } from './carrier-list/carrier-list.component';
import { ChepManagerListComponent } from './chep-manager-list/chep-manager-list.component';
import { ManagePermissionsGuard } from '../core/guards/manage-permissions.guard';
import { CarrierFormComponent } from './carrier-form/carrier-form.component';
import { Routes } from '@angular/router';
import { PermissionListComponent } from './permissions/permission-list.component';
import { UserDashboardComponent } from './user-dashboard.component';
import { AuthGuard } from '@auth0/auth0-angular';
import { ChepManagerFormComponent } from './chep-manager-form/chep-manager-form.component';
import { CarrierManagerListComponent } from './carrier-manager-list/carrier-manager-list.component';
import { CarrierFormType } from './shared/carrier-form-type';
import { PendingChangesGuard } from '../core/guards/pending-changes.guard';
import { ManageDriversGuard } from '../core/guards/manage-drivers.guard';
import { DriverListComponent } from './driver-list/driver-list.component';
import { AdministratorFormComponent } from './administrator-form/administrator-form.component';
import { UserListComponent } from './user-list/user-list.component';
import { RoleComponent } from './role/role.component';
import { RoleListComponent } from './roles/role-list.component';
import { ManageCarriersGuard } from '../core/guards/manage-carriers.guard';
import { CarrierManagerFormComponent } from './carrier-manager-form/carrier-manager-form.component';
import { OverviewGuard } from '../core/guards/overview.guard';
import { SubcontractorListComponent } from './subcontractor-list/subcontractor-list.component';
import { SubcontractorFormComponent } from './subcontractor-form/subcontractor-form.component';
import { SubcontractorDriverListComponent } from './subcontractor-driver-list/subcontractor-driver-list.component';
import { ManageCarrierSubcontractorsGuard } from '../core/guards/manage-carrier-subcontractors.guard';
import { ManageSubcontractorsGuard } from '../core/guards/manage-subcontractors.guard';
import { ManageRegionsGuard } from '../core/guards/manage-regions.guard';
import { RegionListComponent } from './region-list/region-list.component';
import { RegionFormComponent } from './region-form/region-form.component';
import { PlantManagerListComponent } from './plant-manager-list/plant-manager-list.component';
import { PlantManagerFormComponent } from './plant-manager-form/plant-manager-form.component';
import { PodReviewerListComponent } from './pod-reviewer-list/pod-reviewer-list.component';
import { OverdueManagerListComponent } from './overdue-manager-list/overdue-manager-list.component';
import { RegionsResolver } from './shared/resolvers/regions.resolver';
import { ChepServiceRepListComponent } from './chep-service-rep-list/chep-service-rep-list.component';
import { ChepServiceRepFormComponent } from './chep-service-rep-form/chep-service-rep-form.component';
import { BlueyonderUserListComponent } from './blueyonder-user-list/blueyonder-user-list.component';
import { BlueyonderUserFormComponent } from './blueyonder-user-form/blueyonder-user-form.component';
import { ManageBlueyonderUserGuard } from '../core/guards/manage-blueyonder-user-guard.service';
import { SustainabilityManagerListComponent } from './sustainability-manager-list/sustainability-manager-list.component';
import { ManageUserSearchGuard } from '../core/guards/manage-user-search.guard';
import { UserSearchComponent } from './user-search/user-search.component';
import { ManageCarrierManagersGuard } from '../core/guards/manage-carrier-managers-guard.service';

const CARRIERS_ROUTES: Routes = [
  {
    path: 'carriers', canActivate: [ManageCarriersGuard], children: [
      { path: '', component: CarrierListComponent },
      { path: 'new', component: CarrierFormComponent, data: { type: CarrierFormType.CREATE }, canDeactivate: [PendingChangesGuard] },
      {
        path: 'edit/:carrierId', component: CarrierFormComponent, data: { type: CarrierFormType.UPDATE },
        canDeactivate: [PendingChangesGuard]
      },
      {
        path: ':carrierId/managers', children: [
          { path: '', component: CarrierManagerListComponent },
          { path: 'new', component: CarrierManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:managerId', component: CarrierManagerFormComponent, canDeactivate: [PendingChangesGuard] }
        ]
      },
      {
        path: ':carrierId/drivers', children: [
          { path: '', component: CarrierDriverListComponent },
          { path: 'new', component: DriverComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:driverId', component: DriverComponent, canDeactivate: [PendingChangesGuard] }
        ]
      },
      {
        path: ':carrierId/subcontractors', canActivate: [ManageSubcontractorsGuard], children: [
          { path: '', component: SubcontractorListComponent },
          { path: 'new', component: SubcontractorFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:subcontractorId', component: SubcontractorFormComponent, canDeactivate: [PendingChangesGuard] },
          {
            path: ':subcontractorId/drivers', data: { subcontractorMode: true }, children: [
              { path: '', component: SubcontractorDriverListComponent },
              { path: 'new', component: DriverComponent, canDeactivate: [PendingChangesGuard] },
              { path: 'edit/:driverId', component: DriverComponent, canDeactivate: [PendingChangesGuard] }
            ]
          }
        ]
      },
      { path: '**', redirectTo: '' }
    ]
  }
];

const SELF_CARRIER_ROUTES: Routes = [
  {
    path: 'carrier', data: { selfCarrierMode: true }, children: [
      {
        path: 'edit', canActivate: [ManageDriversGuard], component: CarrierFormComponent,
        data: { type: CarrierFormType.UPDATE_OWN }, canDeactivate: [PendingChangesGuard]
      },
      {
        path: 'carrier-manager', canActivate: [ManageCarrierManagersGuard], children: [
          { path: '', component: CarrierManagerListComponent },
          { path: 'new', component: CarrierManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:managerId', component: CarrierManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'drivers', canActivate: [ManageDriversGuard], children: [
          { path: '', component: DriverListComponent },
          { path: 'new', component: DriverComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:driverId', component: DriverComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'subcontractors', canActivate: [ManageCarrierSubcontractorsGuard], children: [
          { path: '', component: SubcontractorListComponent },
          { path: 'new', component: SubcontractorFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:subcontractorId', component: SubcontractorFormComponent, canDeactivate: [PendingChangesGuard] },
          {
            path: ':subcontractorId/drivers', data: { subcontractorMode: true }, children: [
              { path: '', component: SubcontractorDriverListComponent },
              { path: 'new', component: DriverComponent, canDeactivate: [PendingChangesGuard] },
              { path: 'edit/:driverId', component: DriverComponent, canDeactivate: [PendingChangesGuard] }
            ]
          }
        ]
      },
    ]
  }
];

export const USER_DASHBOARD_ROUTES: Routes = [
  {
    path: 'userdashboard', data: { userDashboardView: true }, component: UserDashboardComponent, canActivate: [AuthGuard], children: [
      { path: 'overview', component: OverviewComponent, canActivate: [OverviewGuard] },
      {
        path: 'users', canActivate: [ManageUsersGuard], children: [
          { path: '', component: UserListComponent },
          { path: 'new', component: UserComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:id', component: UserComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'chepManagers', canActivate: [ManageUsersGuard], children: [
          { path: '', component: ChepManagerListComponent, resolve: { regions: RegionsResolver } },
          { path: 'new', component: ChepManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:id', component: ChepManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'plantManagers', canActivate: [ManageUsersGuard], children: [
          { path: '', component: PlantManagerListComponent },
          { path: 'new', component: PlantManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:id', component: PlantManagerFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'administrators', canActivate: [ManageUsersGuard], children: [
          { path: '', component: AdministratorListComponent },
          { path: 'new', component: AdministratorFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:id', component: AdministratorFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'chepServiceRep', canActivate: [ManageUsersGuard], children: [
          { path: '', component: ChepServiceRepListComponent },
          { path: 'new', component: ChepServiceRepFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:id', component: ChepServiceRepFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'podReviewers', canActivate: [ManageUsersGuard], children: [
          { path: '', component: PodReviewerListComponent },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'overdueManagers', canActivate: [ManageUsersGuard], children: [
          { path: '', component: OverdueManagerListComponent },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'sustainabilityManagers', canActivate: [ManageUsersGuard], children: [
          { path: '', component: SustainabilityManagerListComponent },
          { path: '**', redirectTo: '' }
        ]
      },
      // {
      //   path: 'permissions', canActivate: [ManagePermissionsGuard], children: [
      //
      //     { path: '', component: PermissionListComponent },
      //     { path: '**', redirectTo: '' }
      //   ]
      // },
      {
        path: 'userSearch', canActivate: [ManageUserSearchGuard], children: [
          { path: '', component: UserSearchComponent },
          { path: '**', redirectTo: '' }
        ]
      },
      {
        path: 'blueyonder/users', canActivate: [ManageBlueyonderUserGuard], children: [

          { path: '', component: BlueyonderUserListComponent },
          { path: 'new', component: BlueyonderUserFormComponent },
          { path: '**', redirectTo: '' }
        ]
      },
      // {
      //   path: 'roles', canActivate: [ManageRolesGuard], children: [
      //     { path: '', component: RoleListComponent },
      //     { path: 'new', component: RoleComponent, canDeactivate: [PendingChangesGuard] },
      //     { path: 'edit/:id', component: RoleComponent, canDeactivate: [PendingChangesGuard] },
      //     { path: '**', redirectTo: '' }
      //   ]
      // },
      ...CARRIERS_ROUTES,
      ...SELF_CARRIER_ROUTES,
      {
        path: 'regions', canActivate: [ManageRegionsGuard], children: [
          { path: '', component: RegionListComponent },
          { path: 'new', component: RegionFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: 'edit/:regionId', component: RegionFormComponent, canDeactivate: [PendingChangesGuard] },
          { path: '**', redirectTo: '' }
        ]
      },
      { path: '', redirectTo: '/home', pathMatch: 'full' }
    ]
  }
];
